import TimeConstants from "../constants/TimeConstants";

/**
 * Converts a timestamp to a local time string
 * @param {number|string|Date} time - The timestamp to convert
 * @returns {string} The formatted local time string
 */
export const toLocalTimeString = (time) => new Date(time).toString();

/**
 * Formats a timestamp into a pretty date string (e.g., "Jan 1, 2024")
 * @param {number|string|Date} time - The timestamp to format
 * @returns {string} The formatted date string, or empty string if no time provided
 */
export const toPrettyDate = (time) => {
  if (time) {
    const options = { month: "short", day: "numeric", year: "numeric" };
    const date = new Date(time);
    return date.toLocaleDateString("en-US", options);
  }
  return "";
};

/**
 * Checks if a given timestamp is in the future
 * @param {number} time - Unix timestamp in seconds
 * @returns {boolean} True if the timestamp is after the current time
 */
export const isAfterNow = (time) => Math.floor(Date.now() / 1000) < time;

/**
 * Converts a timestamp to UTC midnight
 * @param {number|string|Date} time - The timestamp to convert
 * @returns {number} UTC timestamp in milliseconds
 */
const makeUTC = (time) => {
  const parsedTime = time ? new Date(time) : new Date();
  return Date.UTC(parsedTime.getFullYear(), parsedTime.getMonth(), parsedTime.getDate());
};

/**
 * Calculates the number of days between two dates
 * @param {Object} params - The date parameters
 * @param {number|string|Date} params.start - Start date
 * @param {number|string|Date} params.end - End date
 * @returns {number} Number of days between the dates
 */
export const getDaysBetween = ({ start, end }) => {
  const oneDay = 1000 * 60 * 60 * 24;
  return (makeUTC(end) - makeUTC(start)) / oneDay;
};

/**
 * Formats a timestamp into a human-readable relative time string
 * @param {number} time - Unix timestamp in seconds
 * @returns {Object} Object containing:
 *   @property {string} when - Time relation (NOW, FUTURE, or PAST)
 *   @property {number} time - Numeric value of time difference
 *   @property {string} unitOfTime - Unit of time (years, months, days, etc.)
 *   @property {string} formatted - Formatted string (e.g., "2 days ago", "in 1 hour")
 */
export const formattedFromNow = (time) => {
  const secondsFromNow = time - Math.floor(Date.now() / 1000);
  const tfn = {};

  tfn.when = TimeConstants.NOW;
  if (secondsFromNow > 0) {
    tfn.when = TimeConstants.FUTURE;
  } else if (secondsFromNow < -1) {
    tfn.when = TimeConstants.PAST;
  }

  const absoluteSecondsFromNow = Math.abs(secondsFromNow);

  if (absoluteSecondsFromNow / (60 * 60 * 24 * 365) > 1) {
    tfn.time = Math.floor(absoluteSecondsFromNow / (60 * 60 * 24 * 365));
    tfn.unitOfTime = tfn.time === 1 ? "year" : "years";
  } else if (absoluteSecondsFromNow / (60 * 60 * 24 * 45) > 1) {
    tfn.time = Math.floor(absoluteSecondsFromNow / (60 * 60 * 24 * 30));
    tfn.unitOfTime = tfn.time === 1 ? "month" : "months";
  } else if (absoluteSecondsFromNow / (60 * 60 * 24) > 1) {
    tfn.time = Math.floor(absoluteSecondsFromNow / (60 * 60 * 24));
    tfn.unitOfTime = tfn.time === 1 ? "day" : "days";
  } else if (absoluteSecondsFromNow / (60 * 60) > 1) {
    tfn.time = Math.floor(absoluteSecondsFromNow / (60 * 60));
    tfn.unitOfTime = tfn.time === 1 ? "hour" : "hours";
  } else if (absoluteSecondsFromNow / 60 > 1) {
    tfn.time = Math.floor(absoluteSecondsFromNow / 60);
    tfn.unitOfTime = tfn.time === 1 ? "minute" : "minutes";
  } else {
    tfn.time = Math.floor(absoluteSecondsFromNow);
    tfn.unitOfTime = tfn.time === 1 ? "second" : "seconds";
  }

  if (tfn.when === TimeConstants.FUTURE) {
    return { ...tfn, formatted: `in ${tfn.time} ${tfn.unitOfTime}` };
  }
  if (tfn.when === TimeConstants.PAST) {
    return { ...tfn, formatted: `${tfn.time} ${tfn.unitOfTime} ago` };
  }

  return { ...tfn, formatted: "now" };
};
